import { useMemo } from 'react';
import { useUnrealizedGainLossQuery } from '../api/useUnrealizedGainLossQuery';
import { useRouteParams } from './useRouteParams';
import { useTransactionFilters } from './useTransactionFilters';

export interface Cell {
  text: string | number;
  align?: 'left' | 'right';
  type?: 'currency' | 'number' | 'date' | 'percent';
  fontWeight?: 400 | 600;
  isMobile?: boolean;
}

type LotDetailsRow = {
  id: string;
  cells: Cell[];
};

type ChildRow = {
  id: string;
  cells: Cell[];
  lotDetailsRows: LotDetailsRow[];
};

export type Row = {
  id: string;
  cells: Cell[];
  isTotal?: boolean;
  childRows?: ChildRow[];
};

export interface TableData {
  headers: Cell[];
  rows: Row[];
}

export const useUnrealizedGainLoss = () => {
  const { accountId, groupId, clientId } = useRouteParams();

  const transactionFilters = useTransactionFilters();

  const queryInfo = useUnrealizedGainLossQuery({
    clientId,
    accountId,
    groupId,
    period: transactionFilters.period.value,
    startDate: transactionFilters.period.dates[0],
    endDate: transactionFilters.period.dates[1],
  });

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const tableData = {} as TableData;

    tableData.headers = [
      {
        text: 'Name',
        isMobile: true,
      },
      {
        text: 'Account Number',
      },
      {
        text: 'Ticker',
      },

      {
        text: 'Units',
      },
      {
        text: 'Price',
      },
      {
        text: 'Market Value',
      },
      {
        text: 'Allocation',
      },
      {
        text: 'Cost Basis',
      },

      {
        text: 'Short Term Gain Loss',
      },
      {
        text: 'Long Term Gain Loss',
      },
      {
        text: 'Gain Loss',
        isMobile: true,
      },
      {
        text: 'Acquired Date',
      },
      {
        text: 'Holding Period',
      },
    ];

    tableData.rows = queryInfo.data.map(
      (
        {
          name,
          accountNumber,
          costBasis,
          shortTerm,
          longTerm,
          marketValue,
          percent,
          gainLoss,
          holdingPeriod,
          positions,
        },
        index,
      ) => ({
        id: index.toString(),
        cells: [
          { text: name ?? '', align: 'left', isMobile: true },
          { text: accountNumber ?? '', align: 'right' },
          { text: '', align: 'right' },
          {
            text: '',
            type: 'number',
            align: 'right',
          },
          {
            text: '',
            type: 'number',
            align: 'right',
          },
          {
            text: marketValue ?? '',
            type: 'currency',
            align: 'right',
          },
          {
            text: percent ?? '',
            type: 'percent',
            align: 'right',
          },
          {
            text: costBasis ?? '',
            type: 'currency',
            align: 'right',
          },
          {
            text: shortTerm ?? '',
            type: 'currency',
            align: 'right',
          },
          {
            text: longTerm ?? '',
            type: 'currency',
            align: 'right',
          },
          {
            text: gainLoss ?? '',
            type: 'currency',
            align: 'right',
            isMobile: true,
          },
          {
            text: '',
            align: 'right',
          },
          {
            text: holdingPeriod ?? '',
            align: 'left',
          },
        ] as Cell[],
        childRows: [
          ...positions.map(
            (
              {
                name: posName,
                accountNumber: posAccountNumber,
                ticker,
                units: posUnits,
                price: posPrice,
                costBasis: posCostBasis,
                shortTerm: posShortTerm,
                longTerm: posLongTerm,
                gainLoss: posGainLoss,
                marketValue: posMarketValue,
                percent: posPercent,
                taxLotDetails,
              },
              posIndex,
            ) => ({
              id: posIndex.toString(),
              cells: [
                { text: posName ?? '', align: 'left' },
                { text: posAccountNumber ?? '', align: 'right' },
                { text: ticker ?? '', align: 'right' },
                { text: posUnits ?? '', align: 'right', type: 'number' },
                { text: posPrice ?? '', align: 'right', type: 'currency' },
                { text: posMarketValue ?? '', align: 'right', type: 'currency' },
                { text: posPercent ?? '', align: 'right', type: 'percent' },
                { text: posCostBasis ?? '', align: 'right', type: 'currency' },
                { text: posShortTerm ?? '', align: 'right', type: 'currency' },
                { text: posLongTerm ?? '', align: 'right', type: 'currency' },
                { text: posGainLoss ?? '', align: 'right', type: 'currency' },
                { text: '', align: 'left' },
                { text: '', align: 'left' },
              ] as Cell[],
              lotDetailsRows: [
                ...taxLotDetails.map(
                  (
                    {
                      acquiredDate,
                      costBasis: lotCostBasis,
                      holdingPeriod: lotHoldingPeriod,
                      longTerm: lotLongTerm,
                      marketValue: lotMarketValue,
                      price: lotPrice,
                      percent: lotPercent,
                      shortTerm: lotShortTerm,
                      total,
                      units: lotUnits,
                    },
                    lotIndex,
                  ) => ({
                    id: lotIndex.toString(),
                    cells: [
                      { text: '', align: 'right' },
                      { text: '', align: 'right' },
                      { text: '', align: 'right' },
                      { text: lotUnits ?? '', align: 'right', type: 'number' },
                      { text: lotPrice ?? '', align: 'right', type: 'currency' },
                      { text: lotMarketValue ?? '', align: 'right', type: 'currency' },
                      { text: lotPercent ?? '', align: 'right', type: 'percent' },
                      { text: lotCostBasis ?? '', align: 'right', type: 'currency' },
                      { text: lotShortTerm ?? '', align: 'right', type: 'currency' },
                      { text: lotLongTerm ?? '', align: 'right', type: 'currency' },
                      { text: total ?? '', align: 'right', type: 'currency' },
                      { text: acquiredDate ?? '', align: 'left', type: 'date' },
                      { text: lotHoldingPeriod ?? '', align: 'left' },
                    ] as Cell[],
                  }),
                ),
              ],
            }),
          ),
        ],
      }),
    );

    const totals = queryInfo.data.reduce(
      (acc, item) => {
        acc.shortTerm += item.shortTerm;
        acc.longTerm += item.longTerm;
        acc.total += item.gainLoss;
        return acc;
      },
      { shortTerm: 0, longTerm: 0, total: 0 },
    );

    return { tableData, totals, gainLoss: queryInfo.data };
  }, [queryInfo]);

  return { ...queryInfo, data };
};
